import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, Loader2, ArrowRight, CircleDot } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import api from "@/lib/api";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// Live AI Workforce activity indicator for the builder shell — a compact pill that shows the
// team's recent edits and pulses while a delegation is in flight. Polls lightly so new team
// work (from the Game Director) surfaces without a page reload.
export const WorkforcePulse = ({ gameId, live = false }) => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    if (!gameId) return;
    try {
      const { data } = await api.get(`/workforce/${gameId}/activity`);
      setEntries(data?.entries || []);
    } catch { /* owner-only / not ready — stay silent */ }
    finally { setLoaded(true); }
  }, [gameId]);

  useEffect(() => { load(); }, [load]);

  // Poll faster while a delegation is live, slower otherwise.
  useEffect(() => {
    clearInterval(timer.current);
    timer.current = setInterval(load, live ? 4000 : 30000);
    return () => clearInterval(timer.current);
  }, [load, live]);

  useEffect(() => { if (live) load(); }, [live, load]);

  if (!loaded && !entries.length && !live) return null;

  const recent = entries.slice(0, 6);
  const latest = entries[0];
  const emojis = [...new Set((latest?.agents || []).map((a) => a.emoji))].slice(0, 4);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="workforce-pulse"
          title="AI Workforce — your game's AI team activity"
          className={`relative inline-flex items-center gap-1.5 rounded-full border px-2.5 h-8 text-[11px] font-bold transition-colors ${live ? "border-fuchsia-500/60 bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-300" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}>
          <span className="relative flex items-center">
            {live ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bot className="w-3.5 h-3.5 text-fuchsia-500" />}
            {live && <span className="absolute -right-0.5 -top-0.5 w-1.5 h-1.5 rounded-full bg-fuchsia-500 animate-ping" />}
          </span>
          <span className="hidden sm:inline">AI Team</span>
          {emojis.length > 0 && <span className="tracking-tight leading-none" data-testid="workforce-pulse-emojis">{emojis.join("")}</span>}
          {entries.length > 0 && (
            <span className="ml-0.5 inline-grid place-items-center min-w-[16px] h-[16px] px-1 rounded-full bg-fuchsia-500 text-white text-[9px] leading-none" data-testid="workforce-pulse-count">{entries.length}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 overflow-hidden" data-testid="workforce-pulse-panel">
        <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/40">
          <div className="flex items-center gap-1.5">
            <Bot className="w-4 h-4 text-fuchsia-500" />
            <span className="text-sm font-bold">AI Workforce</span>
            {live && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-fuchsia-500"><CircleDot className="w-3 h-3 animate-pulse" /> working…</span>}
          </div>
          <button onClick={() => { setOpen(false); navigate(`/app/director/${gameId}`); }} data-testid="workforce-pulse-open-director"
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--primary))] hover:underline">
            Open <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        {recent.length === 0 ? (
          <div className="px-4 py-6 text-center" data-testid="workforce-pulse-empty">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No team activity yet.</p>
            <button onClick={() => { setOpen(false); navigate(`/app/director/${gameId}`); }} className="mt-2 text-xs font-semibold text-[hsl(var(--primary))] hover:underline">
              Direct the AI team →
            </button>
          </div>
        ) : (
          <ul className="max-h-72 overflow-y-auto divide-y divide-[hsl(var(--border))]">
            {recent.map((e) => (
              <li key={e.id} className="px-3.5 py-2.5" data-testid={`workforce-pulse-entry-${e.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs font-semibold leading-snug line-clamp-2 flex-1">{e.request}</p>
                  <span className="text-[10px] text-[hsl(var(--muted-foreground))] shrink-0 mt-0.5">{timeAgo(e.created_at)}</span>
                </div>
                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                  {(e.agents || []).map((a, i) => (
                    <span key={i} title={`${a.name}${a.changed ? " · changed" : ""}`}
                      className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${a.changed ? "bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-300" : "bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]"}`}>
                      <span>{a.emoji}</span>{a.name.split(" ")[0]}
                    </span>
                  ))}
                </div>
                {e.merged_change?.summary && (
                  <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-1 font-medium">✓ {e.merged_change.summary}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default WorkforcePulse;
